import { zodResolver } from '@hookform/resolvers/zod';
import { useDialog } from '@musinsa/mds-ui';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useCuratorSnackbar, useFileSizeLimit } from '../../common/hooks';
import {
  getSettlementFormDefaults,
  SETTLEMENT_FORM_SCHEMA,
} from '../components/SettlementInfoForm/settlement-form.schema';
import {
  type BusinessType,
  type SettlementFormData,
  settlementFormSchema,
} from '../components/SettlementInfoForm/types';
import { useAccountValidation } from './useAccountValidation';

type ImageValidationErrors = Partial<
  Record<'identityFile' | 'businessLicenseFile' | 'accountCopyFile', string>
>;

const ACCOUNT_VERIFICATION_REQUIRED_MESSAGE = '계좌 인증을 진행해주세요.';
const ID_NUMBER_ERROR_MESSAGE = '주민번호를 입력하세요.';
const IMAGE_TYPE_ERROR_MESSAGE = '이미지 파일(JPG, PNG, GIF)만 업로드할 수 있습니다.';

const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'] as const;
const ALLOWED_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif'] as const;

const hasAllowedImageType = (file: File): boolean => {
  const mime = file.type.toLowerCase();
  if (mime && ALLOWED_IMAGE_MIME_TYPES.some((type) => type === mime)) {
    return true;
  }
  // 일부 OS/WebView에서는 MIME이 비어있거나 application/octet-stream으로 들어오므로
  // 파일명 확장자로 폴백 검증한다.
  const name = file.name.toLowerCase();
  return ALLOWED_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext));
};

const isValidFile = (value: unknown): value is File => {
  if (typeof File === 'undefined') {
    return Boolean(value);
  }

  return value instanceof File;
};

const extractNumbers = (value: string): string => value.replace(/[^0-9]/g, '');

const formatBusinessRegistrationNumber = (value: string): string => {
  const numbers = extractNumbers(value);
  const maxLength = 10;
  const truncated = numbers.slice(0, maxLength);

  if (truncated.length <= 3) return truncated;
  if (truncated.length <= 5) return `${truncated.slice(0, 3)}-${truncated.slice(3)}`;
  return `${truncated.slice(0, 3)}-${truncated.slice(3, 5)}-${truncated.slice(5)}`;
};

interface UseSettlementFormProps {
  defaultValues?: Partial<SettlementFormData>;
  onSubmit?: (data: SettlementFormData | Partial<SettlementFormData>) => void;
  isEditMode?: boolean;
  hasExistingAccountInfo?: boolean;
}

export const useSettlementForm = ({
  defaultValues,
  onSubmit,
  isEditMode = false,
  hasExistingAccountInfo = false,
}: UseSettlementFormProps = {}) => {
  const snackbar = useCuratorSnackbar();
  const dialog = useDialog();
  const [accountValidationError, setAccountValidationError] = useState<string | null>(null);

  const originalData = useMemo(() => getSettlementFormDefaults(defaultValues), [defaultValues]);

  const typeDataRef = useRef<Record<BusinessType, SettlementFormData | null>>({
    NATURAL_PERSON: null,
    SOLO_PROPRIETORSHIP: null,
    LEGAL_ENTITY: null,
  });
  const [hasUnsavedTypeChange, setHasUnsavedTypeChange] = useState(false);
  const originalType = originalData.businessType;
  const accountFieldsTouchedRef = useRef(false);
  const [isAccountEditing, setIsAccountEditing] = useState(false);
  const accountSnapshotRef = useRef<Pick<
    SettlementFormData,
    'bankName' | 'accountNumber' | 'accountHolder' | 'isAccountVerified' | 'accountCopyFile'
  > | null>(null);
  const idNumberValidationTriggeredRef = useRef(false);
  const imageFileSnapshotsRef = useRef<{
    identityFile: File | null;
    businessLicenseFile: File | null;
    accountCopyFile: File | null;
  }>({
    identityFile: null,
    businessLicenseFile: null,
    accountCopyFile: null,
  });

  const resetIdNumberValidationState = useCallback(() => {
    idNumberValidationTriggeredRef.current = false;
  }, []);

  const isAccountViewMode =
    isEditMode && hasExistingAccountInfo && !hasUnsavedTypeChange && !isAccountEditing;

  const validateImageFiles = useCallback(
    (data: SettlementFormData): ImageValidationErrors | null => {
      const errors: ImageValidationErrors = {};

      if (data.businessType === 'NATURAL_PERSON') {
        if (!isValidFile(data.identityFile)) {
          errors.identityFile = '신분증 이미지를 선택해주세요.';
        }
      } else {
        if (!isValidFile(data.businessLicenseFile)) {
          errors.businessLicenseFile = '사업자 등록증 이미지를 선택해주세요.';
        }
      }

      if (!isValidFile(data.accountCopyFile)) {
        errors.accountCopyFile = '계좌 사본 이미지를 선택해주세요.';
      }

      return Object.keys(errors).length > 0 ? errors : null;
    },
    [],
  );

  const methods = useForm<SettlementFormData>({
    resolver: zodResolver(settlementFormSchema),
    mode: SETTLEMENT_FORM_SCHEMA.validationMode,
    defaultValues: originalData,
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    setError,
    clearErrors,
    reset,
    formState: { errors },
  } = methods;

  const markAccountFieldsAsUnverified = useCallback(() => {
    (['bankName', 'accountNumber', 'accountHolder'] as const).forEach((fieldName) => {
      setError(fieldName, { type: 'custom' });
    });
  }, [setError]);

  const accountValidation = useAccountValidation({
    onSuccess: (data) => {
      if (data?.isValid) {
        setValue('isAccountVerified', true);
        setAccountValidationError(null);
        clearErrors(['bankName', 'accountNumber', 'accountHolder']);

        snackbar.show({
          message: '계좌 인증에 성공하였습니다.',
        });

        return;
      }

      setValue('isAccountVerified', false);
      markAccountFieldsAsUnverified();
      setAccountValidationError(
        data?.errorMessage ?? '계좌 인증에 실패했습니다. 입력 정보를 확인해주세요.',
      );
    },
    onError: () => {
      setValue('isAccountVerified', false);
      setAccountValidationError(null);

      dialog.open({
        title: '시스템 오류로 계좌 인증에 실패하였습니다.',
        message:
          '시스템 오류로 계좌 인증에 실패하였습니다. 문제가 지속되면 앱을 다시 실행하거나 고객센터에 문의해 주세요.',
        confirmButtonText: '확인',
      });
    },
  });

  const createFileSizeLimitHandler = useFileSizeLimit();

  const getFieldsToReset = useCallback((fromType: BusinessType, toType: BusinessType) => {
    const fieldsToReset: Partial<SettlementFormData> = {};

    if (fromType === 'NATURAL_PERSON' && toType !== 'NATURAL_PERSON') {
      fieldsToReset.idNumberFirst = '';
      fieldsToReset.idNumberSecond = '';
      fieldsToReset.identityFile = null;
    }

    if (fromType !== 'NATURAL_PERSON' && toType === 'NATURAL_PERSON') {
      fieldsToReset.businessName = '';
      fieldsToReset.businessRegistrationNumber = '';
      fieldsToReset.businessLicenseFile = null;
    }

    if (fromType !== 'NATURAL_PERSON' && toType !== 'NATURAL_PERSON' && fromType !== toType) {
      fieldsToReset.businessName = '';
      fieldsToReset.businessRegistrationNumber = '';
      fieldsToReset.businessLicenseFile = null;
    }

    fieldsToReset.bankName = '';
    fieldsToReset.accountNumber = '';
    fieldsToReset.accountHolder = '';
    fieldsToReset.isAccountVerified = false;
    fieldsToReset.accountCopyFile = null;

    return fieldsToReset;
  }, []);

  const getChangedFields = useCallback(
    (currentData: SettlementFormData): SettlementFormData | Partial<SettlementFormData> => {
      if (!isEditMode) {
        return currentData;
      }

      const changedFields: Record<string, unknown> = {};
      const imageFields = ['identityFile', 'businessLicenseFile', 'accountCopyFile'] as const;
      const alwaysIncludeFields = ['businessType'] as const;

      (Object.keys(currentData) as Array<keyof SettlementFormData>).forEach((key) => {
        const currentValue = currentData[key];
        const originalValue = originalData[key];

        if (alwaysIncludeFields.includes(key as (typeof alwaysIncludeFields)[number])) {
          changedFields[key] = currentValue;
          return;
        }

        if (imageFields.includes(key as (typeof imageFields)[number])) {
          if (currentValue && currentValue instanceof File) {
            changedFields[key] = currentValue;
          }
          return;
        }

        if (currentValue !== originalValue) {
          changedFields[key] = currentValue;
        }
      });

      if (isAccountViewMode && !accountFieldsTouchedRef.current) {
        // NOTE: view 모드에서 계좌 텍스트 필드는 UI에 노출되지 않으므로 서버 데이터를
        // 덮지 않도록 undefined 처리한다. 단, accountCopyFile 입력은 AccountInfoSection
        // 바깥에 항상 노출되어 사용자가 새로 업로드할 수 있으므로 여기서 덮어쓰지 않는다.
        changedFields.bankName = undefined;
        changedFields.accountNumber = undefined;
        changedFields.accountHolder = undefined;
        changedFields.isAccountVerified = undefined;
      }

      return changedFields as Partial<SettlementFormData>;
    },
    [isEditMode, originalData, isAccountViewMode],
  );

  const handleBusinessTypeChange = useCallback(
    (newType: BusinessType) => {
      const currentData = getValues();
      const currentType = currentData.businessType;

      if (currentType === newType) {
        return;
      }

      typeDataRef.current[currentType] = { ...currentData };

      // 유형 변경 시 현재 계좌 정보를 스냅샷으로 저장 (기존 계좌 정보가 있는 경우)
      if (hasExistingAccountInfo && !hasUnsavedTypeChange) {
        accountSnapshotRef.current = {
          bankName: currentData.bankName,
          accountNumber: currentData.accountNumber,
          accountHolder: currentData.accountHolder,
          isAccountVerified: currentData.isAccountVerified,
          accountCopyFile: currentData.accountCopyFile,
        };
      }

      if (!hasUnsavedTypeChange) {
        setHasUnsavedTypeChange(true);
      }

      const savedData = typeDataRef.current[newType];

      if (savedData) {
        let dataToRestore: typeof originalData | undefined;

        if (newType === originalType) {
          dataToRestore = {
            ...originalData,
            bankName: '',
            accountNumber: '',
            accountHolder: '',
            isAccountVerified: false,
            accountCopyFile: null,
          };
          setHasUnsavedTypeChange(false);
        } else {
          dataToRestore = {
            ...savedData,
            bankName: '',
            accountNumber: '',
            accountHolder: '',
            isAccountVerified: false,
            accountCopyFile: null,
          };
        }

        reset(dataToRestore, {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
        });
        accountFieldsTouchedRef.current = true;
      } else {
        setValue('businessType', newType);
        const fieldsToReset = getFieldsToReset(currentType, newType);
        const currentValues = getValues();
        const newValues = { ...currentValues, ...fieldsToReset };
        reset(newValues, {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
        });
        accountFieldsTouchedRef.current = false;
      }

      setIsAccountEditing(true);

      setAccountValidationError(null);
      clearErrors();
      resetIdNumberValidationState();
    },
    [
      setValue,
      getValues,
      hasUnsavedTypeChange,
      getFieldsToReset,
      setAccountValidationError,
      clearErrors,
      reset,
      originalType,
      resetIdNumberValidationState,
      hasExistingAccountInfo,
    ],
  );

  const confirmTypeChange = useCallback(() => {
    if (hasUnsavedTypeChange) {
      const currentData = getValues();
      const currentType = currentData.businessType;
      typeDataRef.current[currentType] = { ...currentData };

      setHasUnsavedTypeChange(false);
      accountFieldsTouchedRef.current = false;
      if (hasExistingAccountInfo) {
        setIsAccountEditing(false);
        accountSnapshotRef.current = null;
      }
      resetIdNumberValidationState();
    }
  }, [hasUnsavedTypeChange, getValues, hasExistingAccountInfo, resetIdNumberValidationState]);

  const cancelTypeChange = useCallback(() => {
    if (hasUnsavedTypeChange) {
      const savedOriginalData = typeDataRef.current[originalType];
      if (savedOriginalData) {
        reset(savedOriginalData, {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
        });
      }
      setHasUnsavedTypeChange(false);
      accountFieldsTouchedRef.current = false;
      setIsAccountEditing(false);
      accountSnapshotRef.current = null;
      resetIdNumberValidationState();
    }
  }, [hasUnsavedTypeChange, originalType, reset, resetIdNumberValidationState]);

  const handleRequestAccountEdit = useCallback(() => {
    const [bankName, accountNumber, accountHolder, isAccountVerified, accountCopyFile] = getValues([
      'bankName',
      'accountNumber',
      'accountHolder',
      'isAccountVerified',
      'accountCopyFile',
    ]);

    accountSnapshotRef.current = {
      bankName,
      accountNumber,
      accountHolder,
      isAccountVerified,
      accountCopyFile,
    };

    setIsAccountEditing(true);
    setAccountValidationError(null);
    clearErrors(['bankName', 'accountNumber', 'accountHolder']);
    setValue('bankName', '', { shouldDirty: true, shouldTouch: true });
    setValue('accountNumber', '', { shouldDirty: true, shouldTouch: true });
    setValue('accountHolder', '', { shouldDirty: true, shouldTouch: true });
    setValue('isAccountVerified', false);
    setValue('accountCopyFile', accountCopyFile, { shouldDirty: true, shouldTouch: true });
    accountFieldsTouchedRef.current = false;
  }, [clearErrors, getValues, setValue]);

  const handleCancelAccountEdit = useCallback(() => {
    const currentType = getValues().businessType;

    // 유형 변경이 있고 원래 유형으로 돌아온 경우, 전체 유형 변경을 취소하여 view 모드로 복원
    if (hasUnsavedTypeChange && currentType === originalType) {
      cancelTypeChange();
      return;
    }

    // 일반적인 계좌 편집 취소 (유형 변경이 없거나, 유형 변경이 있지만 원래 유형이 아닌 경우)
    const snapshot = accountSnapshotRef.current ?? {
      bankName: originalData.bankName,
      accountNumber: originalData.accountNumber,
      accountHolder: originalData.accountHolder,
      isAccountVerified: originalData.isAccountVerified,
      accountCopyFile: originalData.accountCopyFile ?? null,
    };

    setValue('bankName', snapshot.bankName ?? '', {
      shouldDirty: false,
      shouldTouch: false,
    });
    setValue('accountNumber', snapshot.accountNumber ?? '', {
      shouldDirty: false,
      shouldTouch: false,
    });
    setValue('accountHolder', snapshot.accountHolder ?? '', {
      shouldDirty: false,
      shouldTouch: false,
    });
    setValue('isAccountVerified', snapshot.isAccountVerified ?? false, {
      shouldDirty: false,
      shouldTouch: false,
    });
    setValue('accountCopyFile', snapshot.accountCopyFile ?? null, {
      shouldDirty: false,
      shouldTouch: false,
    });

    setAccountValidationError(null);
    clearErrors(['bankName', 'accountNumber', 'accountHolder']);
    accountFieldsTouchedRef.current = false;
    setIsAccountEditing(false);
    accountSnapshotRef.current = null;
  }, [
    clearErrors,
    originalData,
    setValue,
    hasUnsavedTypeChange,
    cancelTypeChange,
    getValues,
    originalType,
  ]);

  type AccountFieldName = 'bankName' | 'accountNumber' | 'accountHolder';

  const resetAccountValidationState = useCallback(() => {
    accountFieldsTouchedRef.current = true;
    setValue('isAccountVerified', false);
    setAccountValidationError(null);
  }, [setValue]);

  const handleAccountFieldChange = useCallback(
    (fieldName: AccountFieldName, fieldOnChange: (value: string) => void) => (value: string) => {
      fieldOnChange(value);
      resetAccountValidationState();
      clearErrors(fieldName);
    },
    [clearErrors, resetAccountValidationState],
  );

  const handleAccountInputChange = useCallback(
    (fieldName: AccountFieldName, fieldOnChange: (value: string) => void) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        fieldOnChange(value);
        resetAccountValidationState();
        clearErrors(fieldName);
      },
    [clearErrors, resetAccountValidationState],
  );

  type IdNumberFieldName = 'idNumberFirst' | 'idNumberSecond';

  const handleIdNumberChange = useCallback(
    (fieldName: IdNumberFieldName, fieldOnChange: (value: string) => void) => (value: string) => {
      fieldOnChange(value);

      const {
        validation: {
          idNumber: { firstMaxLength: requiredFirstLength, secondMaxLength: requiredSecondLength },
        },
      } = SETTLEMENT_FORM_SCHEMA;

      const isFieldComplete = () => {
        const requiredLength =
          fieldName === 'idNumberFirst' ? requiredFirstLength : requiredSecondLength;

        return extractNumbers(value).length === requiredLength;
      };

      const isCurrentFieldComplete = isFieldComplete();

      if (isCurrentFieldComplete) {
        clearErrors(fieldName);
      } else if (idNumberValidationTriggeredRef.current) {
        setError(fieldName, {
          type: 'manual',
          message: ID_NUMBER_ERROR_MESSAGE,
        });
      }

      const pairedFieldName: IdNumberFieldName =
        fieldName === 'idNumberFirst' ? 'idNumberSecond' : 'idNumberFirst';
      const pairedValue = getValues(pairedFieldName) ?? '';
      const pairedRequiredLength =
        pairedFieldName === 'idNumberFirst' ? requiredFirstLength : requiredSecondLength;
      const pairedLength = extractNumbers(pairedValue).length;
      const isPairedFieldComplete = pairedLength === pairedRequiredLength;

      if (isPairedFieldComplete) {
        clearErrors(pairedFieldName);
      } else if (idNumberValidationTriggeredRef.current) {
        setError(pairedFieldName, {
          type: 'manual',
          message: ID_NUMBER_ERROR_MESSAGE,
        });
      }
    },
    [clearErrors, getValues, setError],
  );

  const handleBusinessRegistrationNumberChange = useCallback(
    (fieldOnChange: (value: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const cursorPos = input.selectionStart || 0;
      const inputValue = e.target.value;

      const formattedValue = formatBusinessRegistrationNumber(inputValue);
      fieldOnChange(formattedValue);

      requestAnimationFrame(() => {
        if (input === document.activeElement) {
          const numbersBeforeCursor = extractNumbers(inputValue.slice(0, cursorPos)).length;
          let newCursorPos = 0;
          let numberCount = 0;

          for (let i = 0; i < formattedValue.length; i++) {
            if (/[0-9]/.test(formattedValue[i])) {
              numberCount++;
              if (numberCount > numbersBeforeCursor) {
                newCursorPos = i;
                break;
              }
            }
            newCursorPos = i + 1;
          }

          input.setSelectionRange(newCursorPos, newCursorPos);
        }
      });

      if (formattedValue.trim()) {
        clearErrors('businessRegistrationNumber');
      }
    },
    [clearErrors],
  );

  const handleVerifyAccount = useCallback(() => {
    const values = getValues(['bankName', 'accountNumber', 'accountHolder']);
    const [bankName, accountNumber, accountHolder] = values.map((value) => value?.trim() || '');

    if (!bankName || !accountNumber || !accountHolder) {
      if (!bankName) {
        setError('bankName', {
          type: 'custom',
          message: '은행을 선택해주세요.',
        });
        setValue('bankName', '', { shouldTouch: true });
      }
      if (!accountNumber) {
        setError('accountNumber', {
          type: 'custom',
          message: '계좌번호를 입력해주세요.',
        });
        setValue('accountNumber', '', { shouldTouch: true });
      }
      if (!accountHolder) {
        setError('accountHolder', {
          type: 'custom',
          message: '예금주를 입력해주세요.',
        });
        setValue('accountHolder', '', { shouldTouch: true });
      }
      return;
    }

    accountValidation.mutate({
      bankName,
      accountNumber,
      accountHolder,
    });
  }, [getValues, setValue, accountValidation]);

  const runFieldValidations = useCallback(
    (data: SettlementFormData) => {
      let hasBlockingError = false;

      const enqueueError = <K extends keyof SettlementFormData>(field: K, message: string) => {
        hasBlockingError = true;
        setError(field, {
          type: 'custom',
          message,
        });
      };

      // NOTE: view 모드에서는 계좌 input이 언마운트되어 사용자가 touch할 경로가
      // 없으므로, 검증을 스킵한다. (getChangedFields 의 view 모드 처리와 일관)
      const shouldValidateAccountFields = !isAccountViewMode;

      if (shouldValidateAccountFields) {
        if (!data.bankName?.trim()) {
          enqueueError('bankName', '은행을 선택해주세요.');
        }
        if (!data.accountNumber?.trim()) {
          enqueueError('accountNumber', '계좌번호를 입력해주세요.');
        }
        if (!data.accountHolder?.trim()) {
          enqueueError('accountHolder', '예금주를 입력해주세요.');
        }
      }

      // NOTE: 주민번호 입력 필드는 edit 모드에서 유형 변경이 없을 때 UI에 노출되지
      // 않으므로 검증도 스킵한다. (SettlementForm 의 렌더 조건과 일관)
      const shouldValidateIdNumberFields = !isEditMode || hasUnsavedTypeChange;

      if (data.businessType === 'NATURAL_PERSON' && shouldValidateIdNumberFields) {
        idNumberValidationTriggeredRef.current = true;
        const {
          validation: {
            idNumber: {
              firstMaxLength: requiredFirstLength,
              secondMaxLength: requiredSecondLength,
            },
          },
        } = SETTLEMENT_FORM_SCHEMA;

        const firstDigits = extractNumbers(data.idNumberFirst?.trim() ?? '');
        const secondDigits = extractNumbers(data.idNumberSecond?.trim() ?? '');

        if (firstDigits.length !== requiredFirstLength) {
          enqueueError('idNumberFirst', ID_NUMBER_ERROR_MESSAGE);
        }
        if (secondDigits.length !== requiredSecondLength) {
          enqueueError('idNumberSecond', ID_NUMBER_ERROR_MESSAGE);
        }
      }

      if (data.businessType !== 'NATURAL_PERSON') {
        if (!data.businessName?.trim()) {
          enqueueError('businessName', '사업자 등록증 상의 상호명을 입력해주세요.');
        }

        const businessNumberDigits = extractNumbers(data.businessRegistrationNumber || '');
        if (!businessNumberDigits) {
          enqueueError('businessRegistrationNumber', '사업자등록번호를 입력해주세요.');
        } else if (businessNumberDigits.length !== 10) {
          enqueueError('businessRegistrationNumber', '사업자등록번호 10자리를 모두 입력해주세요.');
        }
      }

      const imageValidationErrors = validateImageFiles(data);

      if (imageValidationErrors) {
        if (imageValidationErrors.identityFile) {
          enqueueError('identityFile', imageValidationErrors.identityFile);
        }
        if (imageValidationErrors.businessLicenseFile) {
          enqueueError('businessLicenseFile', imageValidationErrors.businessLicenseFile);
        }
        if (imageValidationErrors.accountCopyFile) {
          enqueueError('accountCopyFile', imageValidationErrors.accountCopyFile);
        }
      }

      // 사업자(SOLO_PROPRIETORSHIP, LEGAL_ENTITY)는 계좌 인증 불필요
      if (
        shouldValidateAccountFields &&
        data.bankName &&
        data.accountNumber &&
        data.accountHolder &&
        !data.isAccountVerified &&
        data.businessType === 'NATURAL_PERSON'
      ) {
        hasBlockingError = true;
        markAccountFieldsAsUnverified();
        setAccountValidationError((prev) => prev ?? ACCOUNT_VERIFICATION_REQUIRED_MESSAGE);
      }

      if (!data.termsAgreed) {
        enqueueError('termsAgreed', '정산 정보 수집 이용 동의를 해주세요.');
      }

      return !hasBlockingError;
    },
    [
      validateImageFiles,
      markAccountFieldsAsUnverified,
      setAccountValidationError,
      setError,
      isAccountViewMode,
      isEditMode,
      hasUnsavedTypeChange,
    ],
  );

  const handleFormSubmit = useCallback(() => {
    const submit = handleSubmit(
      (data) => {
        const canSubmit = runFieldValidations(data);
        if (!canSubmit) {
          return;
        }

        if (hasUnsavedTypeChange) {
          confirmTypeChange();
        }

        const submitData = getChangedFields(data);
        // NOTE: RHF가 보관한 File이 중간 경로에서 stale content:// 참조로 바뀌어도
        // 선택 직후 인메모리 복제한 File을 ref에서 꺼내 덮어쓴다.
        // 단, RHF가 해당 필드를 File로 들고 있을 때만 치환해야 유형 변경으로
        // null 처리된 필드를 ref가 부활시키는 것을 막을 수 있다.
        const snapshots = imageFileSnapshotsRef.current;
        const resolvedSubmitData = { ...submitData } as Partial<SettlementFormData>;
        (['identityFile', 'businessLicenseFile', 'accountCopyFile'] as const).forEach((key) => {
          const snapshot = snapshots[key];
          const current = resolvedSubmitData[key];
          if (current instanceof File && snapshot instanceof File) {
            resolvedSubmitData[key] = snapshot;
          }
        });
        onSubmit?.(resolvedSubmitData);
      },
      () => {
        runFieldValidations(getValues());
      },
    );

    submit();
  }, [
    handleSubmit,
    runFieldValidations,
    hasUnsavedTypeChange,
    confirmTypeChange,
    getChangedFields,
    onSubmit,
    getValues,
  ]);

  type ImageFieldName = 'identityFile' | 'businessLicenseFile' | 'accountCopyFile';

  const createImageFileChangeHandler = useCallback(
    (fieldName: ImageFieldName) => {
      const sizeLimitHandler = createFileSizeLimitHandler({
        maxSizeInMB: 5,
        onValid: (file) => {
          imageFileSnapshotsRef.current[fieldName] = file;
          setValue(fieldName, file, { shouldValidate: true });
          clearErrors(fieldName);
        },
        onInvalid: () => {
          imageFileSnapshotsRef.current[fieldName] = null;
          clearErrors(fieldName);
          setValue(fieldName, null, {
            shouldDirty: false,
            shouldTouch: false,
            shouldValidate: false,
          });
        },
      });

      return (event: React.ChangeEvent<HTMLInputElement>) => {
        // accept 속성이 없거나 OS/WebView가 accept를 우회하는 환경을 대비한 클라이언트 검증.
        // 이미지가 아닌 파일은 form 상태에 반영하지 않고 스낵바로만 안내한다.
        const file = event.target.files?.[0];
        if (file && !hasAllowedImageType(file)) {
          snackbar.error(IMAGE_TYPE_ERROR_MESSAGE);
          const input = event.target;
          input.value = '';
          requestAnimationFrame(() => {
            input.value = '';
          });
          return;
        }

        sizeLimitHandler(event);
      };
    },
    [clearErrors, createFileSizeLimitHandler, setValue, snackbar],
  );

  const fileHandlers = useMemo(
    () => ({
      onIdentityFileChange: createImageFileChangeHandler('identityFile'),
      onBusinessLicenseFileChange: createImageFileChangeHandler('businessLicenseFile'),
      onAccountCopyFileChange: createImageFileChangeHandler('accountCopyFile'),
    }),
    [createImageFileChangeHandler],
  );

  return {
    methods,
    errors,
    accountValidation: {
      error: accountValidationError,
      isLoading: accountValidation.isPending,
      isSuccess: accountValidation.isSuccess,
    },
    handlers: {
      onBusinessTypeChange: handleBusinessTypeChange,
      onAccountFieldChange: handleAccountFieldChange,
      onAccountInputChange: handleAccountInputChange,
      onIdNumberChange: handleIdNumberChange,
      onBusinessRegistrationNumberChange: handleBusinessRegistrationNumberChange,
      onVerifyAccount: handleVerifyAccount,
      onSubmit: handleFormSubmit,
      onRequestAccountEdit: handleRequestAccountEdit,
      onCancelAccountEdit: handleCancelAccountEdit,
      ...fileHandlers,
    },
    config: {
      isEditMode,
      hasUnsavedTypeChange,
      confirmTypeChange,
      cancelTypeChange,
      canToggleAccountView:
        hasExistingAccountInfo &&
        (!hasUnsavedTypeChange || getValues().businessType === originalType),
      isAccountViewMode,
    },
  };
};
